<template>
    <span>{{ category }}</span>
</template>
<script setup lang="ts">
/**
 * This is used in the ParticipantSearch component to display the category of a contestant.
 *
 * if a contestant is not defined, we will show the one he matches in the event if an event is provided.
 */
import type { Category, Participant } from '@/objects/generated';
import { useCommonStoreRef, useContestantStore, useEventCommonStoreRef } from '@/stores';
import { getEventAge } from '@/utilities/dateutils';
import { computed } from 'vue';

const props = defineProps<{
    /**
     * The category to display
     */
    value: number | null;

    /**
     * The participant row to display the category for.
     */
    row?: Participant | null;
}>();

const { SportForEvent } = useCommonStoreRef();
const { Event, Categories } = useEventCommonStoreRef();

/**
 * Will present the category of the contestant.
 * If no contestant is found it will try and find the category that matches based on age and gender
 */
const category = computed(() => {
    if (props.value == null) {
        return '';
    }

    const contestant = useContestantStore().GetContestantByParticipantId(props.value);

    if (!contestant || contestant.CategoryID == null) {
        return '';
    }

    if (contestant.CategoryID == null && Event.value != null && SportForEvent.value != null) {
        let eventAge = getEventAge(contestant.Birthday, Event.value?.Date, SportForEvent.value);

        if (eventAge == null) {
            return '';
        }

        let category = Categories.value.find(
            (x: Category) => x.Gender == props.row?.Gender && (x.AgeFrom ?? 0) <= eventAge && (x.AgeTo ?? 0) >= eventAge,
        );

        // if we still dont find the category we will try and check if we have any with the gender BOTH
        if (!category) {
            category = Categories.value.find((x) => x.Gender == 'n' && (x.AgeFrom ?? 0) <= eventAge && (x.AgeTo ?? 0) >= eventAge);
        }

        return category?.Name ?? '';
    } else {
        return Categories.value.find((c: Category) => c.ID === contestant.CategoryID)?.Name ?? '';
    }
});
</script>
